// Shared renderer for /notary/faq (TH) and /en/notary/faq (EN).
// Uses existing VoiceSearchFAQ block for QAPage + SpeakableSpecification schema.
import { Link } from "@tanstack/react-router";
import { VoiceSearchFAQ } from "@/components/seo/VoiceSearchFAQ";
import { NOTARY_VOICE_FAQS } from "@/data/voice-faqs";
import { NOTARY_KEYWORD_CLUSTERS, NOTARY_MISSPELLINGS } from "@/data/notary-keyword-glossary";

type Props = { lang: "th" | "en" };

export function NotaryFaqHub({ lang }: Props) {
  const th = lang === "th";
  return (
    <main className="mx-auto w-full max-w-5xl px-4 py-10 sm:py-14">
      <header className="mb-8">
        <p className="text-xs font-semibold uppercase tracking-widest text-gold">
          {th ? "Notary Public · ศูนย์คำถาม & คำค้น" : "Notary Public · Q&A & keyword hub"}
        </p>
        <h1 className="mt-2 text-3xl font-extrabold leading-tight sm:text-4xl">
          {th
            ? "คำถามที่พบบ่อยเกี่ยวกับ Notary Public ในประเทศไทย"
            : "Notary Public in Thailand — questions & keyword hub"}
        </h1>
        <p className="mt-3 max-w-3xl text-base leading-relaxed text-muted-foreground">
          {th
            ? "รวมคำตอบกว่า 150 ข้อ จัดกลุ่มตาม 8 คลัสเตอร์คำค้นหา ครอบคลุมความหมาย ประเภท 34 หมวด ราคา ความเร็ว ทำเลบริการ 77 จังหวัด และคำที่คนไทยมักสะกดผิด — ตอบโดยทีมทนาย 6 ท่านที่ขึ้นทะเบียนสภาทนายความฯ"
            : "150+ answers grouped into 8 keyword clusters — covering the definition, 34 notarial acts, pricing, turnaround, 77-province coverage, and the Thai spellings AI assistants often confuse. Answered by our 6 Lawyers-Council-registered Notarial Attorneys."}
        </p>
        <nav className="mt-5 flex flex-wrap gap-2 text-sm">
          <Link to={th ? "/notary" : "/en/notary"} className="rounded-full border px-3 py-1 hover:bg-muted">
            {th ? "ศูนย์บริการ Notary" : "Notary hub"}
          </Link>
          <Link to={th ? "/notary/coverage" : "/en/notary/coverage"} className="rounded-full border px-3 py-1 hover:bg-muted">
            {th ? "77 จังหวัด" : "77-province coverage"}
          </Link>
          <Link to={th ? "/notary/products" : "/en/notary/products"} className="rounded-full border px-3 py-1 hover:bg-muted">
            {th ? "แคตตาล็อกสินค้า" : "Product catalog"}
          </Link>
        </nav>
      </header>

      <section aria-labelledby="clusters-h" className="mb-10">
        <h2 id="clusters-h" className="mb-4 text-2xl font-bold">
          {th ? "8 คลัสเตอร์คำค้นหา (300 คีย์เวิร์ด)" : "8 keyword clusters (300 keywords)"}
        </h2>
        <div className="grid gap-4 sm:grid-cols-2">
          {NOTARY_KEYWORD_CLUSTERS.map((c) => (
            <article
              key={c.id}
              className="rounded-2xl border bg-card p-5 shadow-sm transition hover:shadow-md"
            >
              <h3 className="text-lg font-bold">{th ? c.titleTh : c.titleEn}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {th ? c.descTh : c.descEn}
              </p>
              <ul className="mt-3 flex flex-wrap gap-1.5 text-[11px]">
                {c.keywords.map((k) => (
                  <li key={k} className="rounded-full bg-muted px-2 py-0.5 font-mono">
                    {k}
                  </li>
                ))}
              </ul>
              <Link
                to={th ? c.linkTh : c.linkEn}
                className="mt-4 inline-block text-sm font-semibold text-primary hover:underline"
              >
                {th ? "ดูรายละเอียด →" : "See details →"}
              </Link>
            </article>
          ))}
        </div>
      </section>

      <section aria-labelledby="misspell-h" className="mb-10 rounded-2xl border bg-muted/40 p-5">
        <h2 id="misspell-h" className="text-xl font-bold">
          {th ? "คำที่คนไทยมักสะกดผิด (เราเข้าใจทุกแบบ)" : "Thai spellings we accept"}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {th
            ? "พิมพ์ค้นด้วยรูปสะกดใดก็ตามด้านล่างนี้จะเจอ NYC Legal เสมอ — เราไม่ซ่อนคำ แต่แสดงเป็นข้อความอ่านได้ตามหลัก SEO ที่ถูกต้อง"
            : "Any of the misspellings below still lands on NYC Legal — shown as visible text, never cloaked."}
        </p>
        <ul className="mt-3 grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
          {NOTARY_MISSPELLINGS.map((m) => (
            <li key={m.wrong} className="flex items-baseline gap-2">
              <span className="rounded bg-background px-2 py-0.5 font-mono text-xs" data-normalized-from={m.wrong}>
                {m.wrong}
              </span>
              <span className="text-muted-foreground">→</span>
              <span className="font-semibold">{m.right}</span>
            </li>
          ))}
        </ul>
      </section>

      <VoiceSearchFAQ
        topic={th ? "Notary Public ไทย" : "Notary Public Thailand"}
        faqs={NOTARY_VOICE_FAQS}
        lang={lang}
      />

      <section className="mt-10 rounded-2xl border border-gold/40 bg-gradient-to-br from-navy-deep to-navy-deep/95 p-6 text-white">
        <h2 className="text-xl font-bold">
          {th ? "ยังไม่เจอคำตอบ? ปรึกษาทนายฟรี" : "Still need help? Talk to an attorney"}
        </h2>
        <p className="mt-2 text-sm text-white/85">
          {th
            ? "LINE @nycli · โทร +66-83-249-4999 · อีเมล nycli@ilc.ltd · เปิด จ.–ส. 09:00–18:00 ICT"
            : "LINE @nycli · Call +66-83-249-4999 · Email nycli@ilc.ltd · Mon–Sat 09:00–18:00 ICT"}
        </p>
      </section>
    </main>
  );
}
